import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  UserCheck,
  UserX,
  X,
  Check,
  Shield,
  User,
  Package,
} from "lucide-react";
import ApiService from "../../../services/api";
import PatientModal from "./patient-modal";

interface Patient {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  dob: string;
  status: "pending" | "approved" | "rejected";
  is_active: boolean;
  role: string;
  clinic_id: number;
  created_at: string;
  two_fa_preference?: string;
}

interface ExternalPatient {
  _id: string;
  es: {
    FirstName: string;
    PatientID: string;
  };
  es_patient_id_x: string;
}

interface Product {
  product_id: number;
  product_name?: string;
  margin: number;
  price: number;
}

interface PatientsTabProps {
  clinicId: number;
  accountId: string; // Add this for external patients API
}

// NEW: ProductMarginInput Component
const ProductMarginInput = React.memo(
  ({
    product,
    onMarginChange,
  }: {
    product: Product;
    onMarginChange: (id: number, margin: number) => void;
  }) => {
    const [localValue, setLocalValue] = useState(+product.margin);
    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const isTypingRef = useRef(false);

    // Only sync external changes when not actively typing
    useEffect(() => {
      if (!isTypingRef.current) {
        setLocalValue(+product.margin);
      }
    }, [product.margin]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalValue(+value);
      isTypingRef.current = true;

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a longer debounce delay and mark typing as false when done
      timeoutRef.current = setTimeout(() => {
        onMarginChange(product.product_id, +value || 0);
        isTypingRef.current = false;
      }, 3000); // Increased from 300ms to 1000ms
    };

    const handleBlur = () => {
      // Immediately update on blur and stop typing flag
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      onMarginChange(product.product_id, +localValue || 0);
      isTypingRef.current = false;
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
      isTypingRef.current = true;
    };

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
        <span className="text-sm font-medium text-gray-900">
          {product.product_name || `Product ${product.product_id}`}
        </span>
        <div className="flex items-center space-x-2">
          <input
            type="number"
            min="0"
            step="10"
            value={localValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={product?.price?.toString() || "0"}
            className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <span className="text-sm font-medium text-gray-600">$</span>
        </div>
      </div>
    );
  }
);

const PatientsTab: React.FC<PatientsTabProps> = ({ clinicId, accountId }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [externalPatients, setExternalPatients] = useState<ExternalPatient[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [statusApprovalFilter, setApprovalStatusFilter] = useState<string>("");
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedExternalPatient, setSelectedExternalPatient] =
    useState<string>("");
  const [products, setProducts] = useState<Product[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  useEffect(() => {
    fetchPatients();
    fetchExternalPatients();
    initializeProducts();
  }, [clinicId, currentPage, statusFilter, searchTerm, statusApprovalFilter]);

  const fetchPatients = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: any = {
        clinic_id: clinicId,
        page: currentPage,
        pageSize: pageSize,
      };

      // Add search term if provided
      if (searchTerm.trim()) {
        params.search_text = searchTerm.trim();
      }

      // Add status filter if not 'all'
      if (statusFilter === "active") {
        params.is_active = 1;
      } else if (statusFilter === "inactive") {
        params.is_active = 0;
      }

      if (statusApprovalFilter != "") {
        params.status = statusApprovalFilter;
      }

      const result = await ApiService.searchClinicPatients(params);

      if (result.success && result.data) {
        setPatients(result.data);
      } else {
        setError(result.message || "Failed to fetch patients");
      }
    } catch (err: any) {
      console.error("Error fetching patients:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch patients"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExternalPatients = async () => {
    try {
      const result = await ApiService.getExternalPatientsList({
        is_all: 1,
        account_id: accountId,
      });

      if (result.success && result.data) {
        setExternalPatients(result.data);
      }
    } catch (err) {
      console.error("Error fetching external patients:", err);
    }
  };

  const initializeProducts = async () => {
    try {
      const result = await ApiService.searchClinicProducts({
        clinic_id: clinicId,
      });

      if (result.success && result.data) {
        setProducts(result.data);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const handleApprovalAction = async (action: "approved" | "rejected") => {
    if (!selectedPatient) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const requestData: any = {
        clinic_id: clinicId,
        user_id: selectedPatient.user_id,
        action: action,
      };

      if (action === "approved") {
        // if (!selectedExternalPatient) {
        //   setError("Please select an external patient");
        //   return;
        // }
        requestData.external_patient_id = selectedExternalPatient;
        requestData.products = products.map((p) => ({
          product_id: p.product_id,
          margin: p.margin,
        }));
      }

      const result = await ApiService.updatePatientApproval(requestData);

      if (result.success) {
        setIsApprovalModalOpen(false);
        setSelectedPatient(null);
        setSelectedExternalPatient("");
        fetchPatients(); // Refresh the list
      } else {
        setError(result.message || `Failed to ${action} patient`);
      }
    } catch (err: any) {
      console.error(`Error ${action}ing patient:`, err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          `Failed to ${action} patient`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusAction = async (
    userId: number,
    action: "activate" | "deactivate"
  ) => {
    try {
      setError(null);

      const result = await ApiService.updatePatientStatus({
        clinic_id: clinicId,
        user_id: userId,
        action: action,
      });

      if (result.success) {
        fetchPatients(); // Refresh the list
      } else {
        setError(result.message || `Failed to ${action} patient`);
      }
    } catch (err: any) {
      console.error(`Error ${action}ing patient:`, err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          `Failed to ${action} patient`
      );
    }
  };

  const openApprovalModal = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsApprovalModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string, isActive: boolean) => {
    if (status === "pending") {
      return "bg-yellow-100 text-yellow-800";
    }
    if (status === "rejected") {
      return "bg-red-100 text-red-800";
    }
    if (status === "approved") {
      return isActive
        ? "bg-green-100 text-green-800"
        : "bg-red-100 text-red-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  const getStatusIcon = (status: string, isActive: boolean) => {
    if (status === "pending") {
      return <Clock className="h-3 w-3 mr-1" />;
    }
    if (status === "rejected") {
      return <XCircle className="h-3 w-3 mr-1" />;
    }
    if (status === "approved") {
      return isActive ? (
        <CheckCircle className="h-3 w-3 mr-1" />
      ) : (
        <XCircle className="h-3 w-3 mr-1" />
      );
    }
    return null;
  };

  const getDisplayStatus = (status: string, isActive: boolean) => {
    if (status === "pending") return "Pending";
    if (status === "rejected") return "Rejected";
    if (status === "approved") {
      return isActive ? "Active" : "Inactive";
    }
    return status;
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <Shield className="h-4 w-4" />;
      case "clinic":
        return <Users className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "clinic":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (
      e.target.value === "pending" ||
      e.target.value === "approved" ||
      e.target.value === "rejected"
    ) {
      setApprovalStatusFilter(e.target.value);
      setStatusFilter("all");
    } else {
      setApprovalStatusFilter("");
      setStatusFilter(e.target.value);
    }
    setCurrentPage(1); // Reset to first page when filtering
  };

  const productListRef = useRef<HTMLDivElement>(null);

  const updateProductMargin = useCallback(
    (productId: number, margin: number) => {
      // Capture scroll position
      const scrollTop = productListRef.current?.scrollTop || 0;

      setProducts((prev) =>
        prev.map((p) => (p.product_id === productId ? { ...p, margin } : p))
      );

      // Restore scroll position
      setTimeout(() => {
        if (productListRef.current) {
          productListRef.current.scrollTop = scrollTop;
        }
      }, 0);
    },
    []
  );

  // Filter patients based on search and status
  const filteredPatients = patients.filter((patient) => {
    const matchesSearch =
      `${patient.first_name} ${patient.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (statusFilter === "pending") {
      matchesStatus = patient.status === "pending";
    } else if (statusFilter === "approved") {
      matchesStatus = patient.status === "approved";
    } else if (statusFilter === "rejected") {
      matchesStatus = patient.status === "rejected";
    } else if (statusFilter === "active") {
      matchesStatus = patient.status === "approved" && patient.is_active;
    } else if (statusFilter === "inactive") {
      matchesStatus = patient.status === "approved" && !patient.is_active;
    }

    return matchesSearch && matchesStatus;
  });

  // Enhanced Modal Component
  const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = "max-w-lg",
  }: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    maxWidth?: string;
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Enhanced backdrop with blur effect */}
        <div
          className="fixed inset-0 bg-black/7 backdrop-blur-sm transition-all duration-300 ease-out"
          onClick={onClose}
        />

        {/* Modal container with improved positioning and animation */}
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div
            className={`relative transform overflow-hidden rounded-2xl bg-white px-6 py-6 text-left shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:w-full ${maxWidth} sm:p-8`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with improved styling */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 leading-6">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal content */}
            <div>{children}</div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Patients</h3>
          <p className="text-gray-600">Manage patients for this account</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-800 rounded-lg shadow border border-red-200 p-4 mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search patients..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date of Birth
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPatients.map((patient) => (
                <tr key={patient.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <span className="text-sm font-medium text-blue-700">
                          {patient.first_name.charAt(0)}
                          {patient.last_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {patient.first_name} {patient.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {patient.email}
                        </div>
                        {patient.phone_number && (
                          <div className="text-sm text-gray-500">
                            {patient.phone_number}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(patient.dob)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        patient.status,
                        patient.is_active
                      )}`}
                    >
                      {getStatusIcon(patient.status, patient.is_active)}
                      {getDisplayStatus(patient.status, patient.is_active)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                        patient.role
                      )}`}
                    >
                      <span className="mr-1">{getRoleIcon(patient.role)}</span>
                      {patient.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(patient.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {patient.status === "pending" && (
                        <>
                          <button
                            onClick={() => openApprovalModal(patient)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Approve patient"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedPatient(patient);
                              handleApprovalAction("rejected");
                            }}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Reject patient"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {patient.status === "approved" && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedPatient(patient);
                              setOpen(true);
                            }}
                            className="text-blue-600 hover:text-green-900 p-1"
                            title="Edit products"
                          >
                            <Package className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {patient.status === "approved" && (
                        <button
                          onClick={() =>
                            handleStatusAction(
                              patient.user_id,
                              patient.is_active ? "deactivate" : "activate"
                            )
                          }
                          className={`p-1 ${
                            patient.is_active
                              ? "text-red-600 hover:text-red-900"
                              : "text-green-600 hover:text-green-900"
                          }`}
                          title={
                            patient.is_active
                              ? "Deactivate patient"
                              : "Activate patient"
                          }
                        >
                          {patient.is_active ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <PatientModal
            open={open}
            setOpen={setOpen}
            clinicProducts={products}
            clinicId={clinicId}
            patient={selectedPatient}
          />

          {filteredPatients.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No patients found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm
                  ? "Try adjusting your search."
                  : "No patients have been registered for this clinic yet."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Approval Modal */}
      <Modal
        isOpen={isApprovalModalOpen}
        onClose={() => setIsApprovalModalOpen(false)}
        title={`Approve Patient: ${selectedPatient?.first_name} ${selectedPatient?.last_name}`}
        maxWidth="max-w-2xl"
      >
        <div className="space-y-6">
          {/* External Patient Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link to Clinic Patient Record
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Select the corresponding patient from your clinic management
              system based on name and patient ID
            </p>
            <select
              value={selectedExternalPatient}
              onChange={(e) => setSelectedExternalPatient(e.target.value)}
              className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              required
            >
              <option value="">Choose patient from clinic records...</option>
              {externalPatients.map((extPatient) => (
                <option key={extPatient._id} value={extPatient._id}>
                  {extPatient.es.FirstName} - ID: {extPatient.es.PatientID}
                </option>
              ))}
            </select>

            {/* Selected Patient Details Display */}
            {selectedExternalPatient && (
              <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  Selected Patient Details:
                </h4>
                {(() => {
                  const selectedPatientData = externalPatients.find(
                    (p) => p._id === selectedExternalPatient
                  );
                  return selectedPatientData ? (
                    <div className="space-y-1 text-sm text-blue-800">
                      <div>
                        <span className="font-medium">Patient Name:</span>{" "}
                        {selectedPatientData.es.FirstName}
                      </div>
                      <div>
                        <span className="font-medium">Patient ID:</span>{" "}
                        {selectedPatientData.es.PatientID}
                      </div>
                      <div>
                        <span className="font-medium">Embryo Patient ID:</span>{" "}
                        {selectedPatientData.es_patient_id_x}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          {/* Product Margins */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Product Margins
            </label>
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <span className="font-medium">💡 Note:</span> Set additional
                margin amounts for each product. These margins will be added to
                the base product price. You can modify these margins anytime
                after patient approval.
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Example: If base price is $100 and margin is $20, the patient
                will pay $120
              </p>
            </div>
            <div
              ref={productListRef}
              className="space-y-3 max-h-60 overflow-y-auto bg-gray-50 rounded-lg p-4"
            >
              {products.map((product, index) => (
                <ProductMarginInput
                  key={`${product.product_id}-${index}`}
                  product={product}
                  onMarginChange={updateProductMargin}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={() => setIsApprovalModalOpen(false)}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setSelectedPatient(selectedPatient);
              handleApprovalAction("rejected");
            }}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? "Rejecting..." : "Reject"}
          </button>
          <button
            onClick={() => handleApprovalAction("approved")}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Approving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Approve
              </>
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PatientsTab;
